import { Field, ErrorMessage, FormikProps } from 'formik';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { TranslatableFieldLabel } from '@/components/composite/TranslatableFieldLabel';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';

interface QuestionSubtextFieldsProps {
  formik: FormikProps<QuestionFormValues>;
  translateField: (
    sourceKey: string,
    sourceValue: string,
    targetKey: string,
    setFieldValue: any
  ) => void;
  isFieldTranslating: (key: string) => boolean;
  isEditable: boolean;
  language?: 'en' | 'fr';
}

export function QuestionSubtextFields({
  formik,
  translateField,
  isFieldTranslating,
  isEditable,
  language,
}: QuestionSubtextFieldsProps) {
  const { errors, touched, values, setFieldValue } = formik;

  // If language is specified, show only that language's field
  if (language === 'en') {
    return (
      <div className="flex flex-col gap-2">
        <TranslatableFieldLabel
          htmlFor="question_subtext_en"
          onTranslate={() =>
            translateField(
              'question_subtext_en',
              values.question_subtext_en || '',
              'question_subtext_fr',
              setFieldValue
            )
          }
          isTranslating={isFieldTranslating('question_subtext_en')}
        >
          Question Sub-Text
        </TranslatableFieldLabel>
        <Field
          as={Input}
          id="question_subtext_en"
          name="question_subtext_en"
          data-test-id="question-question_subtext_en"
          placeholder="e.g., Panthers @ Falcons"
          disabled={!isEditable}
          className={
            errors.question_subtext_en && touched.question_subtext_en ? 'border-red-500' : ''
          }
        />
        <p className="text-xs text-muted-foreground">
          Optional additional context for the question
        </p>
        <ErrorMessage name="question_subtext_en">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>
    );
  }

  if (language === 'fr') {
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor="question_subtext_fr">Question Sub-Text</Label>
        <Field
          as={Input}
          id="question_subtext_fr"
          name="question_subtext_fr"
          data-test-id="question-question_subtext_fr"
          placeholder="e.g., Panthers @ Falcons"
          disabled={!isEditable}
          className={
            errors.question_subtext_fr && touched.question_subtext_fr ? 'border-red-500' : ''
          }
        />
        <p className="text-xs text-muted-foreground">
          Optional additional context for the question
        </p>
        <ErrorMessage name="question_subtext_fr">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>
    );
  }

  // Fallback: show both languages side-by-side (for backward compatibility)
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="flex flex-col gap-2">
        <TranslatableFieldLabel
          htmlFor="question_subtext_en"
          onTranslate={() =>
            translateField(
              'question_subtext_en',
              values.question_subtext_en || '',
              'question_subtext_fr',
              setFieldValue
            )
          }
          isTranslating={isFieldTranslating('question_subtext_en')}
        >
          Question Sub-Text (English)
        </TranslatableFieldLabel>
        <Field
          as={Input}
          id="question_subtext_en"
          name="question_subtext_en"
          data-test-id="question-question_subtext_en"
          placeholder="e.g., Panthers @ Falcons"
          disabled={!isEditable}
          className={
            errors.question_subtext_en && touched.question_subtext_en ? 'border-red-500' : ''
          }
        />
        <p className="text-xs text-muted-foreground">
          Optional additional context for the question
        </p>
        <ErrorMessage name="question_subtext_en">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="question_subtext_fr">Question Sub-Text (French)</Label>
        <Field
          as={Input}
          id="question_subtext_fr"
          name="question_subtext_fr"
          data-test-id="question-question_subtext_fr"
          placeholder="e.g., Panthers @ Falcons"
          disabled={!isEditable}
          className={
            errors.question_subtext_fr && touched.question_subtext_fr ? 'border-red-500' : ''
          }
        />
        <p className="text-xs text-muted-foreground">
          Optional additional context for the question
        </p>
        <ErrorMessage name="question_subtext_fr">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>
    </div>
  );
}
